import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../../components/SEO';
import RelatedBlogPosts from '../../../components/feature/RelatedBlogPosts';

const ALL_SERVICE_AREAS = [
  { name: 'Toronto', slug: 'toronto' },
  { name: 'Mississauga', slug: 'mississauga' },
  { name: 'Brampton', slug: 'brampton' },
  { name: 'Oakville', slug: 'oakville' },
  { name: 'Hamilton', slug: 'hamilton' },
  { name: 'Richmond Hill', slug: 'richmond-hill' },
  { name: 'Vaughan', slug: 'vaughan' },
  { name: 'Whitby', slug: 'whitby' },
  { name: 'Ajax', slug: 'ajax' },
];

export default function OshawaServiceAreaPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      '@id': 'https://alliancesecuritysystems.com/service-areas/oshawa/#localbusiness',
      name: 'Alliance Security Systems - Oshawa Office',
      description: 'Oshawa\'s trusted security company since 1999. Professional alarm systems, security cameras, access control, and 24/7 live monitoring. 1,500+ local installations across Oshawa, Courtice, Bowmanville, and Durham Region.',
      telephone: '+1-888-458-9181',
      foundingDate: '1999',
      url: 'https://alliancesecuritysystems.com/service-areas/oshawa',
      priceRange: '$$',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Durham Corporate Centre',
        addressLocality: 'Oshawa',
        addressRegion: 'ON',
        postalCode: 'L1H 8L7',
        addressCountry: 'CA',
      },
      geo: { '@type': 'GeoCoordinates', latitude: '43.8971', longitude: '-78.8658' },
      openingHoursSpecification: [
        { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday'], opens: '08:00', closes: '18:00' },
        { '@type': 'OpeningHoursSpecification', dayOfWeek: 'Saturday', opens: '09:00', closes: '14:00' },
      ],
      aggregateRating: { '@type': 'AggregateRating', ratingValue: '4.9', reviewCount: '500', bestRating: '5' },
      areaServed: [
        { '@type': 'City', name: 'Oshawa', containedInPlace: { '@type': 'AdministrativeArea', name: 'Durham Region' } },
        { '@type': 'Neighborhood', name: 'Downtown Oshawa' },
        { '@type': 'Neighborhood', name: 'Northwood' },
        { '@type': 'Neighborhood', name: 'Samac' },
        { '@type': 'Neighborhood', name: 'Lakeview' },
        { '@type': 'Neighborhood', name: 'Windfields' },
        { '@type': 'City', name: 'Courtice' },
        { '@type': 'City', name: 'Bowmanville' },
      ],
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Oshawa Security Services',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Alarm Systems Oshawa', description: 'Professional alarm system installation and monitoring for Oshawa homes and businesses.' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Security Cameras Oshawa', description: 'HD security camera installation with remote viewing for Oshawa properties.' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Access Control Oshawa', description: 'Advanced access control solutions for Oshawa commercial and residential properties.' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: '24/7 Live Monitoring Oshawa', description: 'Round-the-clock professional monitoring services for Oshawa security systems.' } },
        ],
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://alliancesecuritysystems.com' },
        { '@type': 'ListItem', position: 2, name: 'Service Areas', item: 'https://alliancesecuritysystems.com/service-areas' },
        { '@type': 'ListItem', position: 3, name: 'Oshawa', item: 'https://alliancesecuritysystems.com/service-areas/oshawa' },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Do you serve all Oshawa neighborhoods including Downtown and Northwood?',
          acceptedAnswer: { '@type': 'Answer', text: 'Yes! We serve all Oshawa neighborhoods including Downtown Oshawa, Northwood, Samac, Lakeview, Pinecrest, Eastdale, McLaughlin, Taunton, Windfields, Kedron, as well as surrounding communities like Courtice and Bowmanville. Our Durham Region office ensures fast response times across all of Oshawa.' }
        },
        {
          '@type': 'Question',
          name: 'Do you install security systems for Oshawa businesses and commercial properties?',
          acceptedAnswer: { '@type': 'Answer', text: 'Absolutely. Oshawa\'s evolving economy — from its GM/automotive heritage to the growing Ontario Tech University campus and downtown revitalization — creates diverse commercial security needs. We install access control, HD camera systems, and monitored alarms for businesses of all sizes across Oshawa.' }
        },
        {
          '@type': 'Question',
          name: 'How fast is your response time in Oshawa?',
          acceptedAnswer: { '@type': 'Answer', text: 'Our Durham Region office provides response times under 45 minutes for most Oshawa service calls. We have dedicated technicians stationed locally to ensure the fastest possible response for installations, repairs, and emergency service calls throughout Oshawa and surrounding Durham Region communities.' }
        },
      ],
    },
  ];

  const services = [
    { icon: 'ri-alarm-warning-line', title: 'Alarm Systems', description: 'Advanced alarm systems with smart home integration for Oshawa homes and businesses', link: '/alarm-packages' },
    { icon: 'ri-camera-line', title: 'Security Cameras', description: 'HD surveillance cameras with remote viewing for Oshawa residential and commercial properties', link: '/camera-packages' },
    { icon: 'ri-door-lock-line', title: 'Access Control', description: 'Keyless entry and access management systems for Oshawa commercial properties', link: '/access-control' },
    { icon: 'ri-eye-line', title: 'Live Monitoring', description: '24/7 professional monitoring with rapid response for Oshawa security systems', link: '/live-monitoring' },
  ];

  const neighborhoods = [
    'Downtown Oshawa', 'Northwood', 'Samac', 'Lakeview', 'Pinecrest',
    'Eastdale', 'McLaughlin', 'Taunton', 'Windfields', 'Kedron',
    'Courtice', 'Bowmanville',
  ];

  const stats = [
    { number: '1,500+', label: 'Oshawa Installations' },
    { number: '25+', label: 'Years Serving Oshawa' },
    { number: '4.9★', label: 'Customer Rating' },
    { number: '<45min', label: 'Response Time' },
  ];

  const localInsights = [
    {
      icon: 'ri-building-2-line',
      stat: '18,000+',
      description: 'students enrolled at Ontario Tech University and Durham College, creating high demand for student housing security and campus-area residential alarm systems in Oshawa',
    },
    {
      icon: 'ri-store-2-line',
      stat: '$1.2B',
      description: 'invested in Oshawa\'s downtown revitalization since 2015, with new mixed-use developments and businesses requiring modern access control and commercial security solutions',
    },
    {
      icon: 'ri-shield-star-line',
      stat: '31%',
      description: 'increase in Durham Region property crime (2022–2023), making professional alarm systems and monitored security essential for Oshawa homeowners and business owners',
    },
  ];

  const faqs = [
    {
      question: 'Do you serve all Oshawa neighborhoods including Downtown and Northwood?',
      answer: 'Yes! We serve all Oshawa neighborhoods including Downtown Oshawa, Northwood, Samac, Lakeview, Pinecrest, Eastdale, McLaughlin, Taunton, Windfields, Kedron, as well as surrounding communities like Courtice and Bowmanville. Our Durham Region office ensures fast response times across all of Oshawa.',
    },
    {
      question: 'Do you install security systems for Oshawa businesses and commercial properties?',
      answer: 'Absolutely. Oshawa\'s evolving economy — from its GM/automotive heritage to the growing Ontario Tech University campus and downtown revitalization — creates diverse commercial security needs. We install access control, HD camera systems, and monitored alarms for businesses of all sizes across Oshawa.',
    },
    {
      question: 'How fast is your response time in Oshawa?',
      answer: 'Our Durham Region office provides response times under 45 minutes for most Oshawa service calls. We have dedicated technicians stationed locally to ensure the fastest possible response for installations, repairs, and emergency service calls throughout Oshawa and surrounding Durham Region communities.',
    },
  ];

  return (
    <>
      <SEO
        title="Oshawa Security Systems | Alarm & Camera Installation | Alliance Security"
        description="Professional security systems in Oshawa, ON. Alarm installation, security cameras, and access control for Oshawa homes and businesses. Serving Downtown Oshawa, Northwood, Windfields & all Durham Region neighborhoods. 1,500+ local installations. Call 1-888-458-9181."
        keywords="Oshawa security systems, Oshawa alarm systems, Oshawa security cameras, Oshawa access control, security company Oshawa, Oshawa home security, Durham Region security"
        canonicalUrl="/service-areas/oshawa"
        structuredData={structuredData}
      />

      <div className="min-h-screen bg-white">
        {/* Navigation */}
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="flex flex-col leading-none">
                <span className="text-2xl sm:text-3xl font-extrabold tracking-wider text-[#D4A853]" style={{ fontFamily: "'Inter', sans-serif" }}>ALLIANCE</span>
                <span className="text-[10px] sm:text-xs font-semibold tracking-[0.25em] text-[#D4A853]">SECURITY SYSTEMS</span>
              </div>
            </Link>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer">
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl ${isScrolled ? 'text-gray-700' : 'text-white'}`}></i>
            </button>
            <div className="hidden md:flex items-center gap-6">
              <Link to="/" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-[#D4A853]' : 'text-white/90 hover:text-white'}`}>Home</Link>
              <Link to="/service-areas" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-[#D4A853]' : 'text-white'}`}>Service Areas</Link>
              <Link to="/clients" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-[#D4A853]' : 'text-white/90 hover:text-white'}`}>Clients</Link>
              <a href="tel:18884589181" className="bg-[#D4A853] text-white px-6 py-2 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all whitespace-nowrap cursor-pointer">1-888-458-9181</a>
            </div>
          </div>
          {mobileMenuOpen && (
            <div className={`md:hidden ${isScrolled ? 'bg-white' : 'bg-[#0A1628]/95 backdrop-blur-sm'} border-t ${isScrolled ? 'border-gray-100' : 'border-white/10'} px-4 pb-4 space-y-2`}>
              <Link to="/" className={`block py-3 text-sm font-medium border-b ${isScrolled ? 'text-gray-700 border-gray-50' : 'text-white/90 border-white/10'}`}>Home</Link>
              <Link to="/service-areas" className={`block py-3 text-sm font-medium border-b ${isScrolled ? 'text-[#D4A853] border-gray-50' : 'text-[#D4A853] border-white/10'}`}>Service Areas</Link>
              <Link to="/clients" className={`block py-3 text-sm font-medium border-b ${isScrolled ? 'text-gray-700 border-gray-50' : 'text-white/90 border-white/10'}`}>Clients</Link>
              <a href="tel:18884589181" className="block w-full text-center bg-[#D4A853] text-white px-6 py-3 rounded-full text-sm font-semibold mt-2 whitespace-nowrap">1-888-458-9181</a>
            </div>
          )}
        </nav>

        {/* Hero */}
        <section className="relative min-h-[60vh] sm:min-h-[70vh] flex items-center pt-16 sm:pt-20">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/4f0d7eee47de5ff56a1bc3a4dbf8fd87.jpg"
              alt="Oshawa, Ontario"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 w-full py-12 sm:py-20">
            <div className="max-w-3xl">
              <div className="flex items-center gap-2 mb-3 sm:mb-4">
                <i className="ri-map-pin-2-fill text-[#D4A853] text-lg sm:text-xl"></i>
                <span className="text-[#D4A853] font-semibold text-sm sm:text-base">Oshawa, Durham Region</span>
              </div>
              <h1 className="text-3xl sm:text-5xl md:text-6xl font-extrabold text-white mb-4 sm:mb-6 leading-tight">
                <strong>Oshawa Security Systems</strong> — Trusted Protection Since 1999
              </h1>
              <p className="text-base sm:text-xl text-white/90 mb-6 sm:mb-8 leading-relaxed">
                From Oshawa's GM heritage neighbourhoods to the booming Ontario Tech University campus and revitalized downtown core, we've protected over 1,500 Oshawa properties. Our local Durham Region team delivers professional alarm systems, security cameras, and access control — fast.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-8 sm:mb-12">
                <Link to="/#contact" className="inline-flex items-center justify-center gap-2 bg-[#D4A853] text-white px-8 py-4 rounded-full text-base font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap shadow-lg">
                  Get Free Quote <i className="ri-arrow-right-line"></i>
                </Link>
                <a href="tel:18884589181" className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-8 py-4 rounded-full text-base font-semibold hover:bg-white hover:text-[#0A1628] transition-all cursor-pointer whitespace-nowrap">
                  <i className="ri-phone-line"></i> 1-888-458-9181
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Breadcrumb */}
        <nav className="bg-white border-b border-gray-100 py-3" aria-label="Breadcrumb">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <ol className="flex items-center gap-2 text-xs text-gray-500" itemScope itemType="https://schema.org/BreadcrumbList">
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <Link to="/" className="hover:text-[#D4A853] transition-colors cursor-pointer" itemProp="item">
                  <span itemProp="name">Home</span>
                </Link>
                <meta itemProp="position" content="1" />
              </li>
              <li className="flex items-center"><i className="ri-arrow-right-s-line text-gray-400"></i></li>
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <Link to="/service-areas" className="hover:text-[#D4A853] transition-colors cursor-pointer" itemProp="item">
                  <span itemProp="name">Service Areas</span>
                </Link>
                <meta itemProp="position" content="2" />
              </li>
              <li className="flex items-center"><i className="ri-arrow-right-s-line text-gray-400"></i></li>
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <span className="text-[#D4A853] font-medium" itemProp="name">Oshawa</span>
                <meta itemProp="position" content="3" />
              </li>
            </ol>
          </div>
        </nav>

        {/* Stats Bar */}
        <section className="py-6 sm:py-10 bg-[#0A1628]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
              {stats.map((stat, i) => (
                <div key={i} className="text-center">
                  <p className="text-2xl sm:text-4xl font-bold text-[#D4A853] mb-1">{stat.number}</p>
                  <p className="text-white/70 text-xs sm:text-sm">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Local Security Insights */}
        <section className="py-12 sm:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-14">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">OSHAWA SECURITY INSIGHTS</span>
              <h2 className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-3 sm:mb-4">Why Oshawa Properties Need Professional Security</h2>
              <p className="text-sm sm:text-lg text-gray-600 max-w-2xl mx-auto">Local data and trends specific to Oshawa's evolving security landscape</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
              {localInsights.map((insight, i) => (
                <div key={i} className="bg-gray-50 rounded-xl p-6 sm:p-8 border border-gray-100 hover:shadow-lg transition-all">
                  <div className="w-14 h-14 bg-[#D4A853]/10 rounded-full flex items-center justify-center mb-4">
                    <i className={`${insight.icon} text-3xl text-[#D4A853]`}></i>
                  </div>
                  <p className="text-3xl sm:text-4xl font-bold text-[#D4A853] mb-3">{insight.stat}</p>
                  <p className="text-gray-700 leading-relaxed text-sm sm:text-base">{insight.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Services */}
        <section className="py-12 sm:py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-16">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">OSHAWA SECURITY SERVICES</span>
              <h2 className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-3 sm:mb-4">Protecting Oshawa Homes &amp; Businesses</h2>
              <p className="text-sm sm:text-lg text-gray-600 max-w-2xl mx-auto">Comprehensive security solutions for all Oshawa neighborhoods and commercial districts</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {services.map((service, i) => (
                <Link key={i} to={service.link} className="bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-[#D4A853]/30 group">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#D4A853]/10 rounded-full flex items-center justify-center mb-4 sm:mb-6 group-hover:bg-[#D4A853] transition-all">
                    <i className={`${service.icon} text-2xl sm:text-3xl text-[#D4A853] group-hover:text-white transition-colors`}></i>
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-[#0A1628] mb-2 sm:mb-3 group-hover:text-[#D4A853] transition-colors">{service.title}</h3>
                  <p className="text-gray-600 text-xs sm:text-sm leading-relaxed mb-4">{service.description}</p>
                  <div className="flex items-center gap-2 text-[#D4A853] font-semibold text-sm">
                    Learn More <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Related Blog Posts */}
        <RelatedBlogPosts city="oshawa" />

        {/* Why a Local Expert Matters */}
        <section className="py-12 sm:py-24 bg-[#0A1628]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-16">
              <span className="inline-block bg-[#D4A853]/20 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">LOCAL ADVANTAGE</span>
              <h2 className="text-2xl sm:text-4xl font-bold text-white mb-3 sm:mb-4">Why a Local Security Expert Matters in Oshawa</h2>
              <p className="text-sm sm:text-lg text-white/70 max-w-2xl mx-auto">National chains can't match the speed, knowledge, and accountability of a true local provider</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
              {[
                {
                  icon: 'ri-timer-flash-line',
                  title: 'Under 45-Min Response',
                  description: 'Our Durham Region technicians are stationed locally — not dispatched from a distant call centre. Oshawa emergencies get Oshawa-speed responses.',
                },
                {
                  icon: 'ri-government-line',
                  title: 'Oshawa Bylaw Knowledge',
                  description: 'We know Durham Region\'s alarm permit requirements, false alarm bylaws, and Oshawa municipal regulations — so your system stays fully compliant.',
                },
                {
                  icon: 'ri-user-star-line',
                  title: 'Local References',
                  description: '"Alliance secured our Windfields home and our downtown Oshawa office — same team, same quality, and they understood our neighbourhood perfectly." — Oshawa client',
                },
                {
                  icon: 'ri-percent-line',
                  title: 'Ontario Insurance Discounts',
                  description: 'Monitored security systems qualify for up to 20% home insurance discounts in Ontario. Our ULC-certified monitoring satisfies all major insurers.',
                },
              ].map((point, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-6 sm:p-8 hover:bg-white/10 transition-all">
                  <div className="w-12 h-12 bg-[#D4A853]/20 rounded-full flex items-center justify-center mb-4">
                    <i className={`${point.icon} text-2xl text-[#D4A853]`}></i>
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-white mb-2 sm:mb-3">{point.title}</h3>
                  <p className="text-white/65 text-xs sm:text-sm leading-relaxed">{point.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-12 sm:py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-12">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">OSHAWA FAQ</span>
              <h2 className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-3 sm:mb-4">Frequently Asked Questions</h2>
              <p className="text-sm sm:text-lg text-gray-600">Common questions about our Oshawa security services</p>
            </div>
            <div className="space-y-4">
              {faqs.map((faq, i) => (
                <div key={i} className="bg-gray-50 rounded-xl overflow-hidden border border-gray-100">
                  <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-100 transition-colors cursor-pointer">
                    <span className="font-semibold text-[#0A1628] pr-4 text-sm sm:text-base">{faq.question}</span>
                    <i className={`ri-arrow-${openFaq === i ? 'up' : 'down'}-s-line text-2xl text-[#D4A853] flex-shrink-0`}></i>
                  </button>
                  {openFaq === i && (
                    <div className="px-6 pb-5 border-t border-gray-100 pt-4">
                      <p className="text-gray-700 leading-relaxed text-sm sm:text-base">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Neighborhoods */}
        <section className="py-12 sm:py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-14">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">SERVICE COVERAGE</span>
              <h2 className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-3 sm:mb-4">Serving All Oshawa Neighborhoods</h2>
              <p className="text-sm sm:text-lg text-gray-600 max-w-2xl mx-auto">Professional security installation throughout Oshawa and surrounding Durham Region communities</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {neighborhoods.map((n, i) => (
                <div key={i} className="bg-white rounded-lg sm:rounded-xl p-4 sm:p-5 text-center hover:bg-[#D4A853]/5 hover:border-[#D4A853]/30 border border-gray-100 transition-all">
                  <i className="ri-map-pin-line text-[#D4A853] text-xl sm:text-2xl mb-2"></i>
                  <p className="text-[#0A1628] font-semibold text-xs sm:text-sm">{n}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-12 sm:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <div>
                <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">WHY OSHAWA TRUSTS US</span>
                <h2 className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-4 sm:mb-6">Oshawa's Most Trusted Security Company</h2>
                <p className="text-gray-600 mb-6 sm:mb-8 leading-relaxed text-sm sm:text-base">
                  For over 25 years, we've been protecting Oshawa homes and businesses — from the historic McLaughlin neighbourhood to the newest Windfields developments. Our local Durham Region office means faster service and deeper community knowledge.
                </p>
                <div className="space-y-3 sm:space-y-4">
                  {[
                    'Licensed and insured Oshawa security professionals',
                    'Same-day service available across Durham Region',
                    'Factory-certified technicians for all major brands',
                    '24/7 ULC-certified monitoring and support',
                    'Free on-site security assessments in Oshawa',
                    'A+ BBB rating — trusted by 1,500+ Oshawa clients',
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-5 h-5 sm:w-6 sm:h-6 bg-[#D4A853] rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-white text-xs sm:text-sm"></i>
                      </div>
                      <span className="text-gray-700 text-sm sm:text-base">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="relative w-full h-[300px] sm:h-[500px] rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20security%20technician%20installing%20modern%20alarm%20system%20panel%20in%20a%20contemporary%20Oshawa%20Ontario%20home%20interior%20clean%20professional%20workspace%20residential%20security%20installation%20expert&width=800&height=600&seq=oshawa-install-v2&orientation=landscape"
                  alt="Security Installation Oshawa"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Office & Map */}
        <section className="py-12 sm:py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
              <div>
                <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">OSHAWA OFFICE</span>
                <h2 className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-4 sm:mb-6">Serving Oshawa & Durham Region</h2>
                <div className="space-y-4 sm:space-y-6 mb-6 sm:mb-8">
                  {[
                    { icon: 'ri-map-pin-line', label: 'Address', content: 'Durham Corporate Centre\nOshawa, ON L1H 8L7' },
                    { icon: 'ri-phone-line', label: 'Phone', content: '1-888-458-9181', isPhone: true },
                    { icon: 'ri-time-line', label: 'Hours', content: 'Mon–Fri: 8:00 AM – 6:00 PM\nSat: 9:00 AM – 2:00 PM\nSun: By Appointment' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#D4A853]/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className={`${item.icon} text-[#D4A853] text-lg sm:text-xl`}></i>
                      </div>
                      <div>
                        <p className="font-semibold text-[#0A1628] mb-1 text-sm sm:text-base">{item.label}</p>
                        {item.isPhone ? (
                          <a href="tel:18884589181" className="text-[#D4A853] hover:text-[#B8923D] font-semibold text-sm sm:text-base cursor-pointer">{item.content}</a>
                        ) : (
                          <p className="text-gray-600 text-xs sm:text-sm whitespace-pre-line">{item.content}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <Link to="/#contact" className="inline-flex items-center gap-2 bg-[#D4A853] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full text-sm sm:text-base font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap">
                  Schedule Consultation <i className="ri-calendar-line"></i>
                </Link>
              </div>
              <div className="relative w-full h-[300px] sm:h-[500px] rounded-2xl overflow-hidden shadow-xl">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d92358.89234567890!2d-78.8658!3d43.8971!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89d51b5a2f0a5555%3A0x5555555555555555!2sOshawa%2C%20ON!5e0!3m2!1sen!2sca!4v1234567890123!5m2!1sen!2sca"
                  width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade" title="Oshawa Service Area Map"
                ></iframe>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-12 sm:py-24 bg-[#0A1628]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-2xl sm:text-4xl font-bold text-white mb-4 sm:mb-6">Ready to Secure Your Oshawa Property?</h2>
            <p className="text-base sm:text-xl text-white/80 mb-6 sm:mb-8">Get a free consultation and quote for your Oshawa home or business security system</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
              <Link to="/#contact" className="w-full sm:w-auto bg-[#D4A853] text-white px-8 sm:px-10 py-3.5 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap text-center">Get Free Quote</Link>
              <a href="tel:18884589181" className="w-full sm:w-auto border-2 border-white text-white px-8 sm:px-10 py-3.5 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-white hover:text-[#0A1628] transition-all cursor-pointer whitespace-nowrap text-center">Call 1-888-458-9181</a>
            </div>
          </div>
        </section>

        {/* Other Service Areas */}
        <section className="py-12 sm:py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <h2 className="text-xl sm:text-2xl font-bold text-[#0A1628] mb-6 sm:mb-8 text-center">Other GTA Service Areas</h2>
            <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
              {ALL_SERVICE_AREAS.map((area, i) => (
                <Link key={i} to={`/service-areas/${area.slug}`} className="bg-gray-50 hover:bg-[#D4A853]/10 border border-gray-200 hover:border-[#D4A853]/30 px-4 sm:px-6 py-2 sm:py-3 rounded-full text-xs sm:text-sm font-semibold text-[#0A1628] hover:text-[#D4A853] transition-all cursor-pointer whitespace-nowrap">
                  {area.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-[#050B14] text-white py-8 sm:py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6">
              <div className="flex items-center gap-3">
                <div className="flex flex-col leading-none">
                  <span className="text-xl sm:text-2xl font-extrabold tracking-wider text-[#D4A853]" style={{ fontFamily: "'Inter', sans-serif" }}>ALLIANCE</span>
                  <span className="text-[8px] sm:text-[10px] font-semibold tracking-[0.25em] text-[#D4A853]">SECURITY SYSTEMS</span>
                </div>
                <span className="text-white/60 text-xs sm:text-sm">Oshawa Security Experts Since 1999</span>
              </div>
              <div className="flex items-center gap-4 sm:gap-6 flex-wrap justify-center">
                <Link to="/" className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">Home</Link>
                <Link to="/service-areas" className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">Service Areas</Link>
                <Link to="/clients" className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">Clients</Link>
                <Link to="/privacy-policy" className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">Privacy Policy</Link>
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">Powered by Readdy</a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
